import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';

type RemixToolbarEvent = {
	action: 'clicked';
	actionSubject: 'button';
	actionSubjectId: 'remix';
	attributes: {
		remixSource: 'commentPrimaryToolbar';
	};
	eventType: 'ui';
};

export function RemixCommentPrimaryMenuItem({
	api,
	isDisabled,
	remixDisabledReason,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}): React.JSX.Element {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();

	const { isRemixing, areAiEntryPointsDisabled } = useSharedPluginStateWithSelector(
		api,
		['malleableUi', 'aiExperience'],
		(states) => ({
			isRemixing: states.malleableUiState?.isRemixModalOpen || states.malleableUiState?.isThinking,
			areAiEntryPointsDisabled: states.aiExperienceState?.areAiEntryPointsDisabled ?? false,
		}),
	);

	const effectiveDisabled = isDisabled || areAiEntryPointsDisabled;

	const handleClick = useCallback(() => {
		if (effectiveDisabled || isRemixing) {
			return;
		}
		api?.analytics?.actions.fireAnalyticsEvent<RemixToolbarEvent, 'customEventType'>({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'remix',
			eventType: 'ui',
			attributes: {
				remixSource: 'commentPrimaryToolbar',
			},
		});
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'commentPrimaryToolbar',
			},
		});
		api?.core.actions.execute(
			api?.malleableUi?.commands.openRemixModal({
				metadata: { interactionSource: 'remixModalFromToolbar' },
				source: 'commentPrimaryToolbar',
			}),
		);
	}, [api, effectiveDisabled, isRemixing, experienceTracker]);

	const label = formatMessage(messages.remixToolbarLabel);

	const tooltipContent = useMemo(() => {
		if (isRemixing) {
			return formatMessage(messages.remixInProgressTooltip);
		}
		if (effectiveDisabled) {
			return getRemixDisabledTooltip(formatMessage, remixDisabledReason);
		}
		return formatMessage(messages.remixToolbarTooltip);
	}, [isRemixing, effectiveDisabled, formatMessage, remixDisabledReason]);

	return (
		<ToolbarDropdownItem
			elemBefore={<AiGenerativeRemixIcon size="small" label={label} />}
			elemAfter={<div />}
			testId="dropdown-item__malleable-ui-remix-comment"
			isDisabled={effectiveDisabled || isRemixing}
			title={tooltipContent}
			onClick={handleClick}
		>
			{label}
		</ToolbarDropdownItem>
	);
}
